import PrimaryButton from '@/components/Buttons/PrimaryButton';
import useThemeColors from '@/hooks/useThemeColors';
import { CalendarEvent } from '@/types';
import { Clock, User, Repeat, Video } from 'lucide-react';
import React, { useState } from 'react';
import { cleanWordPressHtml } from '@/utils/cleanHtml';

interface Props {
    event: CalendarEvent;
}

const EventCard: React.FC<Props> = ({ event }) => {
    const colors = useThemeColors();
    const [showDetails, setShowDetails] = useState(false);

    const getRecurrenceLabel = (type: string, interval: number) => {
        const intervalLabel = interval <= 1 ? 'Semanal' : `Cada ${interval} semanas`;

        switch (type) {
            case 'weekly_unlimited':
                return intervalLabel;
            case 'weekly_until_date':
                return `${intervalLabel} (limitado)`;
            default:
                return null;
        }
    };

    const recurrenceLabel = getRecurrenceLabel(event.recurrence_type, event.recurrence_interval);

    return (
        <>
            <div
                onClick={() => setShowDetails(true)}
                className={`cursor-pointer rounded border-l-2 px-1 py-0.5 text-[9px] transition-colors sm:px-1.5 sm:py-1 sm:text-[11px] ${colors.border} ${colors.accentBg} ${colors.headerHover}`}
            >
                <div className="mb-0.5 flex items-center gap-0.5 sm:gap-1">
                    <span className={`font-medium ${colors.accentText}`}>{event.time}</span>
                    {recurrenceLabel ? (
                        <Repeat className={`ml-auto h-2 w-2 sm:h-2.5 sm:w-2.5 ${colors.iconColor}`} />
                    ) : null}
                </div>
                <p className="line-clamp-2 font-medium leading-tight text-gray-900">{event.title}</p>
            </div>

            {/* Modal */}
            {showDetails ? (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-4"
                    onClick={() => setShowDetails(false)}
                >
                    <div
                        className="w-full max-w-lg rounded-lg bg-white p-6"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="mb-6 flex items-start justify-between">
                            <h3 className="text-xl font-semibold text-gray-900">{event.title}</h3>
                            <button
                                onClick={() => setShowDetails(false)}
                                className="rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                            >
                                <svg
                                    className="h-5 w-5"
                                    fill="none"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth="2"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path d="M6 18L18 6M6 6l12 12"></path>
                                </svg>
                            </button>
                        </div>

                        <div className="space-y-4">
                            <div className="flex items-center gap-3">
                                <Clock className="h-5 w-5 text-gray-400" />
                                <div>
                                    <p className="text-sm font-medium text-gray-900">
                                        {event.date} • {event.time}
                                    </p>
                                </div>
                            </div>

                            {recurrenceLabel ? (
                                <div className="flex items-center gap-3">
                                    <Repeat className="h-5 w-5 text-gray-400" />
                                    <p className="text-sm text-gray-700">{recurrenceLabel}</p>
                                </div>
                            ) : null}

                            {event.professor?.full_name && (
                                <div className="flex items-center gap-3">
                                    <User className="h-5 w-5 text-gray-400" />
                                    <p className="text-sm text-gray-700">{event.professor.full_name}</p>
                                </div>
                            )}

                            {event.google_meet_url ? (
                                <div className="flex items-center gap-3">
                                    <Video className="h-5 w-5 text-gray-400" />
                                    <a
                                        href={event.google_meet_url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className={`text-sm font-medium ${colors.accentText} hover:underline`}
                                    >
                                        Unirse a Google Meet
                                    </a>
                                </div>
                            ) : null}

                            {event.description ? (
                                <div className="mt-4 border-t border-gray-200 pt-4">
                                    <div
                                        className="text-sm text-gray-700"
                                        dangerouslySetInnerHTML={{ __html: cleanWordPressHtml(event.description) }}
                                    />
                                </div>
                            ) : null}
                        </div>

                        <div className="mt-6 flex justify-end">
                            <PrimaryButton
                                type="button"
                                onClick={() => setShowDetails(false)}
                                size="md"
                            >
                                Cerrar
                            </PrimaryButton>
                        </div>
                    </div>
                </div>
            ) : null}
        </>
    );
};

export default EventCard;
